"use client"

import React, { useState, useRef, useEffect } from "react"
import { Link } from "react-router-dom"
import { Box, Flex, Text, Avatar, Card, Separator } from "@radix-ui/themes"
import { User, LogOut, ChevronDown } from "lucide-react"
import { useLanguage } from "../contexts/LanguageContext"
import { useRestaurantAuth } from "../contexts/RestaurantAuthContext"
import { restaurantPortalAPI } from "../services/restaurantApiClient"

interface ProfileDropdownProps {
  user: {
    username?: string
    name?: string
    fullName?: string
    profileImage?: string
    role?: string
  } | null
  onLogout: () => void
  profilePath: string
  isRestaurant?: boolean
  restaurantId?: string
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  user,
  onLogout,
  profilePath,
  isRestaurant = false,
  restaurantId
}) => {
  const { t, language } = useLanguage()
  const { user: restaurantUser } = useRestaurantAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [restaurantData, setRestaurantData] = useState<any>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Fetch restaurant data for logo if it's a restaurant
  useEffect(() => {
    if (isRestaurant && restaurantId) {
      restaurantPortalAPI.getRestaurantInfo(restaurantId)
        .then(response => {
          const restaurant = response.data?.restaurant || response.data
          setRestaurantData(restaurant)
        })
        .catch(error => {
          console.error("Error fetching restaurant data:", error)
        })
    }
  }, [isRestaurant, restaurantId])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false)
    }
  }

  const actualProfilePath = isRestaurant && restaurantId
    ? `/restaurant/${restaurantId}/profile`
    : profilePath

  // Get user name and avatar
  const userName = isRestaurant
    ? (restaurantUser?.name || user?.name || user?.username || "User")
    : (user?.fullName || user?.username || user?.name || "User")

  const restaurantImage = restaurantData?.image || restaurantData?.logo || restaurantUser?.restaurant?.logo
  const userProfileImage = !isRestaurant && user?.profileImage ? user.profileImage : undefined
  const avatarSrc = isRestaurant ? (restaurantImage || undefined) : userProfileImage
  const avatarFallback = isRestaurant
    ? (restaurantUser?.restaurant?.name?.charAt(0).toUpperCase() || userName.charAt(0).toUpperCase() || "R")
    : (userName.charAt(0).toUpperCase() || "U")

  const getRoleDisplay = () => {
    if (isRestaurant) {
      if (restaurantUser?.role && restaurantUser.role !== 'restaurant_owner') {
        return restaurantUser.role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
      }
      return restaurantUser?.restaurant?.name || t("restaurantOwner");
    }
    return user?.role || t("user");
  }

  return (
    <Box style={{ position: 'relative' }} ref={dropdownRef as any}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '8px',
          borderRadius: 'var(--radius-3)',
          border: 'none',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          transition: 'background-color 0.2s'
        }}
        className="hover:bg-[var(--gray-3)]"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Avatar
          size="2"
          src={avatarSrc}
          fallback={avatarFallback}
          color="red"
        />
        <Box className="hidden md:block" style={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
          <Text size="2" weight="medium" as="div">
            {userName}
          </Text>
          <Text size="1" color="gray">
            {getRoleDisplay()}
          </Text>
        </Box>
        <ChevronDown
          className="w-4 h-4"
          style={{
            color: 'var(--gray-9)',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.2s'
          }}
        />
      </button>

      {isOpen && (
        <Card
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            [language === 'ar' ? 'left' : 'right']: 0,
            width: '192px',
            zIndex: 50,
            boxShadow: 'var(--shadow-5)',
            padding: '4px'
          }}
        >
          <Link
            to={actualProfilePath}
            onClick={() => setIsOpen(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: language === 'ar' ? 'row-reverse' : 'row',
              gap: '12px',
              padding: '8px 16px',
              borderRadius: 'var(--radius-2)',
              textDecoration: 'none',
              color: 'var(--gray-12)',
              fontSize: '14px',
              transition: 'background-color 0.2s'
            }}
            className="hover:bg-[var(--gray-3)]"
          >
            <User className="w-4 h-4" style={{ color: 'var(--gray-9)' }} />
            <Text size="2">{t('myProfile')}</Text>
          </Link>

          <Separator size="4" my="1" />

          <button
            onClick={() => {
              setIsOpen(false)
              onLogout()
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: language === 'ar' ? 'row-reverse' : 'row',
              gap: '12px',
              width: '100%',
              padding: '8px 16px',
              borderRadius: 'var(--radius-2)',
              border: 'none',
              backgroundColor: 'transparent',
              color: 'var(--gray-12)',
              fontSize: '14px',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              textAlign: language === 'ar' ? 'right' : 'left'
            }}
            className="hover:bg-[var(--gray-3)]"
          >
            <LogOut className="w-4 h-4" style={{ color: 'var(--gray-9)' }} />
            <Text size="2">{t('logout')}</Text>
          </button>
        </Card>
      )}
    </Box>
  )
}

export default ProfileDropdown

